import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaStar, 
  FaUsers, 
  FaClock, 
  FaChild, 
  FaCheckCircle,
  FaChevronLeft,
  FaChevronRight,
  FaCalendarAlt,
  FaUserGraduate,
  FaBriefcase,
  FaQuoteLeft,
  FaQuoteRight
} from 'react-icons/fa';
import coursesData from '../data/coursesData';

const CourseDetailPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [relatedCourses, setRelatedCourses] = useState([]);
  
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Find the course by ID
    const foundCourse = coursesData.find(c => c.id === courseId);
    
    if (foundCourse) {
      setCourse(foundCourse);
      
      // Find related courses in the same category
      const related = coursesData
        .filter(c => c.category === foundCourse.category && c.id !== courseId)
        .slice(0, 3);
        
      setRelatedCourses(related);
    }
    
    setLoading(false);
  }, [courseId]);
  
  // Handle tab switching
  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };
  
  // Handle next/previous course navigation
  const handleNavigateCourse = (direction) => {
    // Find current course index
    const currentIndex = coursesData.findIndex(c => c.id === courseId);
    
    if (currentIndex !== -1) {
      let nextIndex;
      
      if (direction === 'next') {
        nextIndex = (currentIndex + 1) % coursesData.length;
      } else {
        nextIndex = (currentIndex - 1 + coursesData.length) % coursesData.length;
      }
      
      navigate(`/courses/${coursesData[nextIndex].id}`);
    }
  };

  // Helper function to get course image path
  const getCourseImagePath = (course) => {
    // Map course categories to specific images
    const categoryImages = {
      'Robotics': '/images/programs/course/bot.jpg',
      'Programming': '/images/programs/course/web.jpg',
      'Electronics': '/images/programs/course/pp.png',
      'Science': '/images/programs/course/level1.jpg',
      'Mathematics': '/images/programs/course/dp.png',
      'Game Development': '/images/programs/course/1.png'
    };

    // Get specific image based on course ID if available
    const courseImages = {
      'robotics-beginners': '/images/programs/course/level1.jpg',
      'robotics-advanced': '/images/programs/course/level2.jpg',
      'coding-fundamentals': '/images/programs/course/web.jpg',
      'electronics-workshop': '/images/programs/course/pp.png',
      'science-explorers': '/images/programs/course/13.png',
      'game-development': '/images/programs/course/15.png',
      'math-problem-solving': '/images/programs/course/dp.png'
    };

    // Use specific course image if available, otherwise use category image, or fallback to default
    return courseImages[course.id] || categoryImages[course.category] || '/images/programs/course/bot.jpg';
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!course) {
    return (
      <div className="container mx-auto py-16 px-4">
        <div className="text-center py-12 bg-white rounded-lg shadow-md">
          <div className="text-5xl mb-4">🔍</div>
          <h3 className="text-2xl font-bold text-gray-700 mb-2">Course Not Found</h3>
          <p className="text-gray-500 mb-6">The course you're looking for doesn't exist or has been removed.</p>
          <Link 
            to="/courses"
            className="px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
          >
            Browse All Courses
          </Link>
        </div>
      </div>
    );
  }
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { 
        duration: 0.5 
      }
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen pb-16">
      {/* Course Header */}
      <div className="py-20 px-4" style={{ background: 'linear-gradient(to right, #202c45, #3f5c8f)' }}>
        <div className="container mx-auto">
          <div className="flex items-center text-white mb-6">
            <Link to="/courses" className="flex items-center hover:underline">
              <FaChevronLeft className="mr-1" /> Back to Courses
            </Link>
            
            <div className="ml-auto flex space-x-2">
              <button 
                className="p-2 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-colors"
                onClick={() => handleNavigateCourse('prev')}
                aria-label="Previous course"
              >
                <FaChevronLeft className="text-white" />
              </button>
              <button 
                className="p-2 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-colors"
                onClick={() => handleNavigateCourse('next')}
                aria-label="Next course"
              >
                <FaChevronRight className="text-white" />
              </button>
            </div>
          </div>
          
          <motion.div 
            className="flex flex-col md:flex-row md:items-center gap-6"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div 
              className="w-full md:w-1/3 lg:w-1/4"
              variants={itemVariants}
            >
              <div className="bg-white p-2 rounded-lg shadow-lg">
                <img 
                  src={getCourseImagePath(course)} 
                  alt={course.title}
                  className="w-full h-full object-cover rounded"
                />
              </div>
            </motion.div>
            
            <motion.div
              className="w-full md:w-2/3 lg:w-3/4 text-white"
              variants={itemVariants}
            >
              <div className="flex flex-wrap gap-2 mb-3">
                <span className="bg-white bg-opacity-20 text-white text-sm font-medium px-3 py-1 rounded-full">
                  {course.category}
                </span>
                <span className="bg-white bg-opacity-20 text-white text-sm font-medium px-3 py-1 rounded-full">
                  {course.level}
                </span>
              </div>
              
              <h1 className="text-3xl md:text-4xl font-bold mb-3">{course.title}</h1>
              
              <div className="flex items-center gap-6 mb-4 flex-wrap">
                <div className="flex items-center">
                  <FaStar className="text-yellow-400 mr-1" />
                  <span>{course.rating}</span>
                </div>
                <div className="flex items-center">
                  <FaUsers className="mr-1" />
                  <span>{course.studentsEnrolled}+ enrolled</span>
                </div>
                <div className="flex items-center">
                  <FaClock className="mr-1" />
                  <span>{course.duration}</span>
                </div>
                <div className="flex items-center">
                  <FaChild className="mr-1" />
                  <span>{course.ageGroup}</span>
                </div>
              </div>
              
              <p className="text-white text-opacity-90 mb-6">
                {course.shortDescription}
              </p>
              
              <div className="flex items-center gap-4">
                <span className="text-2xl font-bold">{course.price}</span>
                <a 
                  href="https://docs.google.com/forms/d/e/1FAIpQLSfcR5SGMKi9XrdgFROEUukUL5oINfiQQy-cnezcsjt0KbSHOQ/viewform"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 bg-red-600 text-white font-medium rounded-full hover:bg-red-700 transition-colors"
                >
                  Apply Now
                </a>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
      
      {/* Course Content */}
      <div className="container mx-auto px-4 -mt-6">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Navigation Tabs */}
          <div className="flex overflow-x-auto">
            <button 
              className={`px-6 py-4 font-medium text-gray-700 whitespace-nowrap ${
                activeTab === 'overview' 
                  ? 'border-b-2 border-blue-600 text-blue-600' 
                  : 'hover:text-blue-600'
              }`}
              onClick={() => handleTabClick('overview')}
            >
              Overview
            </button>
            <button 
              className={`px-6 py-4 font-medium text-gray-700 whitespace-nowrap ${
                activeTab === 'curriculum' 
                  ? 'border-b-2 border-blue-600 text-blue-600' 
                  : 'hover:text-blue-600'
              }`}
              onClick={() => handleTabClick('curriculum')}
            >
              Curriculum
            </button>
            <button 
              className={`px-6 py-4 font-medium text-gray-700 whitespace-nowrap ${
                activeTab === 'instructors' 
                  ? 'border-b-2 border-blue-600 text-blue-600' 
                  : 'hover:text-blue-600'
              }`}
              onClick={() => handleTabClick('instructors')}
            >
              Instructors
            </button>
            <button 
              className={`px-6 py-4 font-medium text-gray-700 whitespace-nowrap ${
                activeTab === 'testimonials' 
                  ? 'border-b-2 border-blue-600 text-blue-600' 
                  : 'hover:text-blue-600'
              }`}
              onClick={() => handleTabClick('testimonials')}
            >
              Testimonials
            </button>
          </div>
          
          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">Course Description</h2>
                  <div className="prose max-w-none text-gray-600">
                    {course.fullDescription?.split('\n').map((paragraph, idx) => (
                      <p key={idx} className="mb-4">{paragraph}</p>
                    ))}
                  </div>
                </div>
                
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">What You'll Learn</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {course.learningOutcomes?.map((outcome, idx) => (
                      <div key={idx} className="flex items-start">
                        <FaCheckCircle className="text-blue-600 mt-1 mr-2 flex-shrink-0" />
                        <span className="text-gray-700">{outcome}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">Course Materials</h2>
                  <ul className="list-disc list-inside text-gray-700 space-y-2">
                    {course.materials?.map((material, idx) => (
                      <li key={idx}>{material}</li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            )}
            
            {/* Curriculum Tab */}
            {activeTab === 'curriculum' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Course Curriculum</h2>
                
                <div className="space-y-4">
                  {course.curriculum?.map((week, idx) => (
                    <div 
                      key={idx} 
                      className="border border-gray-200 rounded-lg overflow-hidden"
                    >
                      <div className="flex justify-between items-center bg-gray-50 px-4 py-3">
                        <h3 className="font-medium text-gray-800">
                          <span className="font-bold">Week {week.week}:</span> {week.title}
                        </h3>
                        <div className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                          {week.topics.length} topics
                        </div>
                      </div>
                      <div className="p-4">
                        <ul className="list-disc list-inside text-gray-700 space-y-1">
                          {week.topics.map((topic, topicIdx) => (
                            <li key={topicIdx}>{topic}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
            
            {/* Instructors Tab */}
            {activeTab === 'instructors' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Meet Your Instructors</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {course.instructors?.map((instructor, idx) => (
                    <div key={idx} className="bg-gray-50 p-6 rounded-lg">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white text-xl font-bold">
                          {instructor.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-800">{instructor.name}</h3>
                          <div className="flex items-center text-gray-600 text-sm">
                            <FaUserGraduate className="mr-1" />
                            {instructor.qualification}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start text-gray-700 mb-2">
                        <FaBriefcase className="mt-1 mr-2 flex-shrink-0 text-blue-600" />
                        <span>{instructor.experience}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
            
            {/* Testimonials Tab */}
            {activeTab === 'testimonials' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Student Testimonials</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {course.testimonials?.map((testimonial, idx) => (
                    <div key={idx} className="bg-gray-50 p-6 rounded-lg">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center text-white text-xl font-bold">
                          {testimonial.name.split(' ')[0][0]}
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-800">{testimonial.name}</h3>
                          <div className="text-gray-600 text-sm">
                            {testimonial.relation}
                          </div>
                        </div>
                      </div>
                      <div className="relative text-gray-700 italic">
                        <FaQuoteLeft className="absolute -top-2 -left-2 text-gray-300 opacity-50 text-xl" />
                        <p className="relative z-10 pl-4">{testimonial.comment}</p>
                        <FaQuoteRight className="absolute -bottom-2 -right-2 text-gray-300 opacity-50 text-xl" />
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </div>
        
        {/* Related Courses */}
        {relatedCourses.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Related Courses</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedCourses.map(relatedCourse => (
                <div 
                  key={relatedCourse.id}
                  className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="relative h-48 bg-gray-200">
                    <img 
                      src={getCourseImagePath(relatedCourse)} 
                      alt={relatedCourse.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-0 right-0 bg-blue-600 text-white text-xs font-bold px-2 py-1 m-2 rounded">
                      {relatedCourse.level}
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-2">{relatedCourse.title}</h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{relatedCourse.shortDescription}</p>
                    
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-blue-600">{relatedCourse.price}</span>
                      <Link 
                        to={`/courses/${relatedCourse.id}`}
                        className="px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
                      >
                        View Details
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Call to Action */}
        <div className="mt-12 rounded-xl p-8 text-center text-white" style={{ background: 'linear-gradient(to right, #202c45, #3f5c8f)' }}>
          <h2 className="text-2xl font-bold mb-4">Ready to start your STEM journey?</h2>
          <p className="mb-6 max-w-2xl mx-auto">
            Join our community of young innovators and build the skills needed for future success.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="https://docs.google.com/forms/d/e/1FAIpQLSfcR5SGMKi9XrdgFROEUukUL5oINfiQQy-cnezcsjt0KbSHOQ/viewform"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-red-600 text-white font-medium rounded-full hover:bg-red-700 transition-colors"
            >
              Apply for this Course
            </a>
            <Link 
              to="/contact"
              className="px-6 py-3 bg-white text-blue-600 font-medium rounded-full hover:bg-gray-100 transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetailPage; 